import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Waveform } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatErr } from "@/lib/api";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import Logo from "@/components/Logo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await register(email.trim(), password, name.trim());
      toast.success("Welcome to Flowscribe");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      <div className="hidden lg:flex lg:col-span-2 bg-[--accent] text-white p-10 flex-col justify-between">
        <Link to="/" className="flex items-center">
          <Logo className="h-9" variant="inverse" />
        </Link>
        <div>
          <div className="overline text-white/85 mb-4">NEW ACCOUNT</div>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-[0.95]">
            Speech customized
            <br />
            for your business.
          </h2>
          <p className="mt-4 text-white/90 max-w-sm leading-relaxed">
            Transcribe, translate, and re-cast dialects in minutes. Powered by Flowscribe's multi-engine STT.
          </p>
        </div>
        <div className="font-mono text-xs text-white/80">4 speech engines · 27 languages · 50+ dialects</div>
      </div>

      <div className="lg:col-span-3 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center mb-10">
            <Logo className="h-8" />
          </Link>
          <div className="overline text-[--muted] mb-4">CREATE&nbsp;ACCOUNT</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Start transcribing.</h1>

          <GoogleLoginButton label="Sign up with Google" testId="google-register-btn" />

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-[--line]"></div>
            <span className="overline text-[--muted]">OR</span>
            <div className="h-px flex-1 bg-[--line]"></div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="overline block mb-2">Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="Ada Lovelace"
              />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="you@studio.com"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="min 6 chars"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="register-submit-btn"
              className="w-full bg-[--ink] text-white py-3 text-sm font-bold hover:bg-[--accent] transition-colors disabled:opacity-50"
            >
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[--muted]">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login" className="text-[--ink] font-semibold underline underline-offset-4 hover:text-[--accent]">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
